export const naturallySortByTitle = <T extends { title: string }>(items: T[], priorityTitles: string[] = []): T[] => {
  return [...items].sort((a, b) => {
    const isAPriority = priorityTitles.includes(a.title);
    const isBPriority = priorityTitles.includes(b.title);

    if (isAPriority && !isBPriority) {
      return 1;
    }
    if (!isAPriority && isBPriority) {
      return -1;
    }

    const aParts = a.title.split(/(\d+)/);
    const bParts = b.title.split(/(\d+)/);

    if (aParts[0] !== bParts[0]) {
      return a.title.localeCompare(b.title);
    }

    for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
      const aNum = parseInt(aParts[i]);
      const bNum = parseInt(bParts[i]);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum !== bNum) {
          return aNum - bNum;
        }
      } else if (aParts[i] !== bParts[i]) {
        return aParts[i].localeCompare(bParts[i]);
      }
    }
    return a.title.localeCompare(b.title);
  });
};

type BuildingWithHouseholds = {
  buildingId: number;
  street?: string | null;
  households: { title: string }[];
};

const firstHouseholdNumber = (building: BuildingWithHouseholds): number => {
  const numbers = building.households
    .map((household) => household.title.match(/\d+/)?.[0])
    .filter((number): number is string => number !== undefined)
    .map(Number);

  return numbers.length > 0 ? Math.min(...numbers) : Number.POSITIVE_INFINITY;
};

export const sortBuildingsByHouseholdSequence = <T extends BuildingWithHouseholds>(buildings: T[]): T[] => {
  return [...buildings].sort((a, b) => {
    const firstHouseholdA = firstHouseholdNumber(a);
    const firstHouseholdB = firstHouseholdNumber(b);
    if (firstHouseholdA !== firstHouseholdB) {
      return firstHouseholdA - firstHouseholdB;
    }

    const streetDifference = (a.street ?? "").localeCompare(b.street ?? "", undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return streetDifference !== 0 ? streetDifference : a.buildingId - b.buildingId;
  });
};

// First flat in sidebar order, e.g. Skanste -> "Dzīvoklis 01" of Grostonas iela 9 B.
// Used to land on a development when it is picked in the switcher.
export const firstHouseholdId = (
  buildings: (BuildingWithHouseholds & { households: { id: number; title: string }[] })[],
  priorityTitles: string[] = ["Haldur"],
): number | undefined => {
  for (const building of sortBuildingsByHouseholdSequence(buildings)) {
    const [first] = naturallySortByTitle(building.households, priorityTitles);
    if (first) {
      return first.id;
    }
  }
  return undefined;
};
